<script>
import { mapGetters } from 'vuex/dist/vuex.common.js';

export default {
  name: 'LabelArchiveContentsBadge',
  props: {
    labelArchive: Object,
    large: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('packingSettings', [
      'getUnitToUnitRelation',
      'getLowerLevelUnit',
      'getHigherLevelUnit',
    ]),
    ...mapGetters('emptyLabelArchive', [
      'isLabelArchiveEmpty',
      'emptyAmount',
    ]),
    ...mapGetters('debug', [
      'debug',
    ]),
    lowerUnit() {
      return this.getLowerLevelUnit(this.labelArchive.unitId, this.labelArchive.skuId);
    },
    higherUnit() {
      return this.getHigherLevelUnit(this.labelArchive.unitId, this.labelArchive.skuId);
    },
    lowerChildren() {
      if (!this.lowerUnit) return [];
      return this.labelArchive.containedLabels.filter(
        label => label.unitId === this.lowerUnit.id,
      );
    },
    emptyLowerChildrenAmount() {
      if (!this.lowerUnit) return 0;
      return this.emptyAmount(this.lowerChildren.map(l => l.id));
    },
    lowerUnitChildrenAmount() {
      if (!this.lowerUnit) return 0;
      return this.lowerChildren.length - this.emptyLowerChildrenAmount;
    },
    maxAmount() {
      if (!this.lowerUnit) return 0;
      return this.getUnitToUnitRelation(
        this.lowerUnit.id,
        this.labelArchive.unitId,
        this.labelArchive.skuId,
      )?.amount || 0;
    },
    isFull() {
      if (!this.lowerUnit) return false;
      return this.lowerUnitChildrenAmount >= this.maxAmount;
    },
    emptyAvaiableAmount() {
      if (!this.lowerUnit) return 0;
      return this.maxAmount - this.lowerChildren.length;
    },
    emptyItems() {
      return this.lowerChildren.filter(item => this.isLabelArchiveEmpty(item.id));
    },
    notEmptyItems() {
      return this.lowerChildren.filter(item => !this.isLabelArchiveEmpty(item.id));
    },
    lockedLowerChildrenRelations() {
      return this.lowerChildren.filter(l => l.locked)
        .filter(l => !this.isLabelArchiveEmpty(l.id))
        .length;
    },
  },

};
</script>

<template>
  <div
    v-if="lowerUnit"
    class="d-inline-flex label-archive-contents-badge"
    :class="{
      'label-archive-contents-badge--large': large,
    }"
  >
    <Tippy
      v-if="emptyLowerChildrenAmount > 0 && debug"
      arrow
      placement="bottom"
      boundary="window"
    >
      <template #trigger>
        <BBadge
          v-b-tooltip.top
          variant="danger"
          class="ml-1"
        >
          <i class="fas fa-hand-holding-box mr-1 fa-fw" />
          {{ emptyLowerChildrenAmount }}
        </BBadge>
      </template>

      <div class="d-flex flex-column align-items-center">
        <p class="mb-1 text-left small">
          Puste miejsca  wynikające z odłączenia<br>
          elementów podrzędnych ręcznie:
        </p>
        <p class="mb-0 font-weight-bold">
          {{ emptyLowerChildrenAmount }}
        </p>
      </div>
    </Tippy>
    <BBadge
      v-else-if="emptyLowerChildrenAmount > 0"
      variant="danger"
      class="ml-1 label-archive-contents-badge"
    >
      <i class="fas fa-hand-holding-box mr-1 fa-fw" />
      {{ emptyLowerChildrenAmount }}
    </BBadge>

    <Tippy
      v-if="debug"
      arrow
      :max-width="900"
      placement="bottom"
      boundary="window"
    >
      <template #trigger>
        <BBadge
          :variant="isFull ? 'secondary' : 'success'"
          class="ml-1"
        >
          <i
            class="fas mr-1 fa-fw"
            :class="{
              'fa-box-open': !isFull,
              'fa-box': isFull,
            }"
          />
          {{ lowerUnitChildrenAmount }}/{{ maxAmount }}
        </BBadge>
      </template>

      <div>
        <div class="mb-2 text-left small">
          Agregacja tego elementu:
        </div>

        <div class="d-flex">
          <div
            v-if="emptyItems.length > 0"
            class="d-flex flex-column align-items-center mr-4"
          >
            <p class="mb-0 font-weight-bold">
              {{ emptyItems.length }}
            </p>
            <small class="no-wrap">
              <i class="fas fa-hand-holding-box mr-1" />
              ręcznie usunięte
            </small>
          </div>

          <div
            v-if="lockedLowerChildrenRelations > 0 && emptyItems.length > 0"
            class="mr-4"
          >
            +
          </div>

          <div
            v-if="lockedLowerChildrenRelations > 0"
            class="d-flex flex-column align-items-center mr-4"
          >
            <p class="mb-0 font-weight-bold">
              {{ lockedLowerChildrenRelations }}
            </p>
            <small class="no-wrap">
              <i class="fas fa-lock mr-1" />
              zablokowane
            </small>
          </div>

          <div
            v-if="emptyItems.length > 0
              && (lockedLowerChildrenRelations > 0 || emptyItems.length > 0)"
            class="mr-4"
          >
            +
          </div>

          <div
            v-if="notEmptyItems.length - lockedLowerChildrenRelations > 0"
            class="d-flex flex-column align-items-center mr-4"
          >
            <p class="mb-0 font-weight-bold">
              {{ notEmptyItems.length - lockedLowerChildrenRelations }}
            </p>
            <small class="no-wrap">
              <i class="fas fa-list-ol mr-1" />
              dodane przez kolejność
            </small>
          </div>

          <div
            v-if="emptyAvaiableAmount > 0
              && (lockedLowerChildrenRelations > 0
                || emptyItems.length > 0
                || notEmptyItems.length - lockedLowerChildrenRelations > 0)"
            class="mr-4"
          >
            +
          </div>

          <div
            v-if="emptyAvaiableAmount > 0"
            class="d-flex flex-column align-items-center mr-4"
          >
            <p class="mb-0 font-weight-bold">
              {{ emptyAvaiableAmount }}
            </p>
            <small class="no-wrap">
              <i class="fas fa-arrow-right-to-line mr-1" />
              wolne miejsca
            </small>
          </div>

          <div class="mr-4">
            =
          </div>

          <div class="d-flex flex-column align-items-center">
            <p class="mb-0 font-weight-bold">
              {{ maxAmount }}
            </p>
            <small>
              pojemność
            </small>
          </div>
        </div>
      </div>
    </Tippy>
    <BBadge
      v-else
      :variant="isFull ? 'secondary' : 'success'"
      class="ml-1 label-archive-contents-badge"
    >
      <i
        class="fas mr-1 fa-fw"
        :class="{
          'fa-box-open': !isFull,
          'fa-box': isFull,
        }"
      />
      {{ lowerUnitChildrenAmount }}/{{ maxAmount }}
    </BBadge>
  </div>
</template>

<style lang="scss">
.label-archive-contents-badge {
  font-size: 15px;
  line-height: 1.3;
  display: flex;
  align-items: center;
  justify-content: center;

  &--large {
    font-size: 15px;
  }
}
</style>
